import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { ToastrService } from 'ngx-toastr';
import { take } from 'rxjs/operators';
import { User } from '../model/user';
import { getStoredUser } from '../helpers/user-storage.util';
import { MiscService } from '../services/misc.service';
import { UserService } from '../services/user.service';
import { CommonUtilityServiceService } from '../services/common-utility-service.service';
import { DocumentService } from '../services/document.service';
import { ServiceCapabilityPickerComponent } from '../service-capability-picker/service-capability-picker.component';
import {
  ZoneDocUploadDialogComponent,
  ZoneDocUploadResult,
} from '../zone-documents/zone-doc-upload-dialog/zone-doc-upload-dialog.component';
import { ZoneDocCoverage } from '../zone-doc-coverage-form/zone-doc-coverage-form.component';
import {
  filterServiceList,
  licenseNodesUnderService,
  licenseRowState,
  zoneLicenseRequiredCount,
  zoneLicenseRows,
  zoneLicenseUnderReviewCount,
  zoneHasLicenseRows,
  LicenseRequirement,
  LicenseRow,
} from '../zone-documents/zone-doc.util';
import { slugify } from '../shared/testid/testid.util';

/**
 * Services & Zones — the vendor's service-capability + coverage-zone management
 * surface, lifted out of the legacy 4-tab manage-account mega-component into its
 * own routed page (CONNECT 2.0, Spec 6 · Task 1). The CON-140 functional design
 * is unchanged: the shared service-capability-picker drives Section 1's offered
 * capabilities and the unified Add/Edit zone form; the read view renders one card
 * per zone with license-status chips + red "documents required" dots.
 *
 * The page is self-sufficient — it fetches its own service-type catalog, state /
 * county details and applicable license requirements rather than depending on
 * manage-account's in-memory state, so the two surfaces are fully decoupled.
 */
@Component({
  selector: 'app-services-zones',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatSelectModule,
    MatIconModule,
    MatMenuModule,
    ServiceCapabilityPickerComponent,
    ZoneDocUploadDialogComponent,
  ],
  templateUrl: './services-zones.component.html',
  styleUrl: './services-zones.component.scss',
})
export class ServicesZonesComponent implements OnInit {
  user: User = {};

  // Service-type catalog + coverage-zone working state.
  serviceZoneStateCountyDetails: any = [];
  serviceZoneCounties: any = [];
  serviceTypes: any = [];
  toggleNewServiceZone: boolean = false;
  // The Add-a-Zone form's INDEPENDENT working copy of the offered capabilities.
  // Deep-cloned from offeredServiceTemplate each time the form opens so the
  // vendor's per-zone enabledForZone edits never mutate user.selectedServiceTypes
  // and Section 1's own template reassignments can't clobber an in-progress Add
  // selection.
  newZoneServiceTypes: any = [];
  // The vendor's offered capabilities (their selectedServiceTypes) — the source
  // the Add form is seeded from. Section 1's per-item persistence handlers
  // maintain THIS list (add/remove services + specialties) rather than the Add
  // form's working copy.
  offeredServiceTemplate: any = [];
  selectedNewZoneState: any = '';
  selectedNewCounties: any = [];
  currentServiceZone: any = '';

  editZoneToggle: boolean = false;
  // The ORIGINAL zone object (the same identity the read-view *ngFor iterates)
  // currently being edited. Used to render the unified Edit form in place of that
  // zone's read card — identity match against the loop item, not a value compare.
  editingZone: any = null;

  // Zone-document upload dialog context. The shared zone-doc-upload-dialog owns
  // the file/preview/coverage-form UI; this host owns only the per-node context
  // (which zone/service/sub-service the upload is scoped to) and the submit flag.
  zoneDocUploadOpen: boolean = false;
  zoneDocUploadZone: any = null;
  zoneDocUploadService: any = null;
  zoneDocUploadSubType: any = null;
  zoneDocUploadSiblings: any[] = [];
  zoneDocUploadLicense: LicenseRequirement | null = null;
  zoneDocUploadHasServiceLevelNode: boolean = true;
  zoneDocUploadSubmitting: boolean = false;

  // License requirements applicable to this PA (GET /license-requirements),
  // resolved server-side against their offered services. These drive the
  // read-view document-status chips + red dots. `serviceTypeIdToType` bridges
  // each association's serviceTypeId (UUID) to the service `type` key the zone
  // nodes use, built from the service-type catalog.
  applicableLicenses: LicenseRequirement[] = [];
  serviceTypeIdToType: Record<string, string> = {};

  constructor(
    private miscService: MiscService,
    private toastr: ToastrService,
    private userService: UserService,
    private utilityService: CommonUtilityServiceService,
    private documentService: DocumentService,
  ) {}

  ngOnInit(): void {
    this.user = getStoredUser();

    // MiscService.fetch* are ReplaySubject-backed and never complete; take(1)
    // ensures each subscription resolves once and tears itself down (a forkJoin
    // over them would hang).
    this.miscService
      .fetchStateCountyDetails()
      .pipe(take(1))
      .subscribe((result: any) => {
        const updatedStateCounties = (result ?? []).map(
          (stateCountyDetail: any) => {
            return {
              ...stateCountyDetail,
              counties: ['All Counties', ...stateCountyDetail.counties],
            };
          },
        );
        this.serviceZoneStateCountyDetails = [
          ...updatedStateCounties,
          { state: 'NATIONWIDE', name: 'Nationwide', counties: [] },
        ];
      });

    // Applicable license requirements for this PA — the source of the read-view
    // document-status chips + red dots.
    this.documentService
      .fetchApplicableLicenseRequirements()
      .pipe(take(1))
      .subscribe((licenses: any[]) => {
        this.applicableLicenses = licenses ?? [];
      });

    this.miscService
      .fetchServiceTypes()
      .pipe(take(1))
      .subscribe((result: any) => {
        if (result) {
          // Bridge each service type's id (associations key on serviceTypeId) to
          // its immutable `type` key (zone nodes + selectedServiceTypes key on
          // `type`).
          const idToType: Record<string, string> = {};
          for (const serviceType of result) {
            if (serviceType?.id && serviceType?.type) {
              idToType[serviceType.id] = serviceType.type;
            }
          }
          this.serviceTypeIdToType = idToType;
          this.serviceTypes = result.map((serviceType: any) => {
            const userSelectedServiceType =
              this.user?.selectedServiceTypes?.find(
                (ust: any) => ust.type === serviceType.type,
              );
            let serviceSubTypes = serviceType.serviceSubTypes;
            if (serviceSubTypes) {
              serviceSubTypes = serviceSubTypes
                .filter((sst: any) => sst.enabled)
                .map((sst: any) => {
                  const selected =
                    userSelectedServiceType?.serviceSubTypes?.find(
                      (usst: any) => usst.type === sst.type,
                    )?.enabled || false;
                  return {
                    ...sst,
                    // `checked` is the section's existing selection flag; the
                    // shared picker drives `enabledForZone`. Seed both from the
                    // same source and keep them mirrored (see
                    // onServiceCapabilitiesChange).
                    checked: selected,
                    enabledForZone: selected,
                  };
                });
            }
            const serviceSelected = userSelectedServiceType?.enabled || false;
            return {
              ...serviceType,
              checked: serviceSelected,
              enabledForZone: serviceSelected,
              showSubTypes: serviceSelected && serviceSubTypes?.length > 0,
              serviceSubTypes: serviceSubTypes,
            };
          });
          // Seed the offered-capabilities template from a DEEP COPY of the
          // vendor's selected services so neither the Add form's working copy nor
          // Section 1's reassignments ever mutate user.selectedServiceTypes by
          // shared identity.
          this.offeredServiceTemplate = this.cloneOfferedCapabilities(
            this.user?.selectedServiceTypes || [],
          );
        }
      });
  }

  decodeState(state: string): string {
    return this.utilityService.decodeUsState(state);
  }

  formatCounty(counties: any) {
    return counties?.join(', ');
  }

  // Stable, non-presentational testid part for a zone (state key or NATIONWIDE).
  zoneTestKey(serviceZone: any): string {
    return slugify(
      serviceZone?.nationwide ? 'nationwide' : serviceZone?.state ?? 'zone',
    );
  }

  // Stable testid part for a service card — keyed on the service `type` (the
  // same field the outstanding-license lookups match on), not the
  // user-visible `displayName`.
  serviceTestKey(service: any): string {
    return slugify(service?.type || service?.displayName || 'service');
  }

  // Stable testid part for one outstanding-license menu entry, keyed on the
  // license id (deduplicated upstream by `outstandingLicensesForServiceCard`).
  licenseTestKey(license: any): string {
    return slugify(String(license?.id ?? license?.documentTitle ?? 'license'));
  }

  // Read-view derivations, composed from the shared zone-doc.util so this page
  // computes requirement state identically to the Documents surface.
  filterServiceList(services: any) {
    return filterServiceList(services);
  }

  // The applicable license rows for one zone.
  private zoneRows(serviceZone: any): LicenseRow[] {
    return zoneLicenseRows(
      serviceZone,
      this.applicableLicenses,
      this.serviceTypeIdToType,
    );
  }

  // A zone has document rows when it has at least one applicable requirement.
  /**
   * Whether this zone's services carry ANY license requirement at all.
   *
   * Also drives the zone status badge's fourth state, and that distinction is
   * the point: "every requirement is met" and "there are no requirements" both
   * arrive with a required count of 0 and an under-review count of 0, so the
   * cleared badge used to read "All licenses on file" for a zone that has no
   * licenses on file and never needed any. A zone with no requirement rows has
   * nothing to be on file, so it says so instead.
   */
  zoneHasDocNodes(serviceZone: any): boolean {
    return zoneHasLicenseRows(
      serviceZone,
      this.applicableLicenses,
      this.serviceTypeIdToType,
    );
  }

  // Zone summary: count of license rows still outstanding.
  zoneRequiredCount(serviceZone: any): number {
    return zoneLicenseRequiredCount(
      serviceZone,
      this.applicableLicenses,
      this.serviceTypeIdToType,
    );
  }

  // Zone summary: how many license rows currently have a document under review.
  zoneUnderReviewCount(serviceZone: any): number {
    return zoneLicenseUnderReviewCount(
      serviceZone,
      this.applicableLicenses,
      this.serviceTypeIdToType,
    );
  }

  zoneRequiredLabel(serviceZone: any): string {
    const count = this.zoneRequiredCount(serviceZone);
    return `${count} license${count === 1 ? '' : 's'} required`;
  }

  zoneUnderReviewLabel(serviceZone: any): string {
    const count = this.zoneUnderReviewCount(serviceZone);
    return `${count} license${count === 1 ? '' : 's'} under review`;
  }

  // Whether the SERVICE-LEVEL requirement (subType null) is still outstanding —
  // drives the per-service red dot on the read-view chip head.
  serviceLevelIsOutstanding(serviceZone: any, service: any): boolean {
    return this.zoneRows(serviceZone).some(
      (row) =>
        row.service?.type === service?.type &&
        !row.subType &&
        licenseRowState(serviceZone, row) === 'required',
    );
  }

  // Whether an enabled sub-service still has an outstanding license requirement —
  // drives the per-sub-service red dot on the read-view sub-chip.
  subServiceIsOutstanding(
    serviceZone: any,
    service: any,
    subType: any,
  ): boolean {
    return this.zoneRows(serviceZone).some(
      (row) =>
        row.service?.type === service?.type &&
        row.subType?.type === subType?.type &&
        licenseRowState(serviceZone, row) === 'required',
    );
  }

  // Whether a read-view service card still shows a red "document required" icon —
  // the service-level dot OR any enabled sub-service dot. Gates the per-service
  // "Add License" button.
  serviceCardHasOutstandingLicense(serviceZone: any, service: any): boolean {
    return (
      this.serviceLevelIsOutstanding(serviceZone, service) ||
      this.enabledSubTypes(service).some((subType: any) =>
        this.subServiceIsOutstanding(serviceZone, service, subType),
      )
    );
  }

  // The distinct license requirements still outstanding under one service card,
  // deduplicated by license id. Drives what the "Add License" click opens: the
  // single requirement directly, or a menu naming each one when there is > 1.
  outstandingLicensesForServiceCard(
    serviceZone: any,
    service: any,
  ): LicenseRequirement[] {
    const result: LicenseRequirement[] = [];
    const seen = new Set<string>();
    for (const row of this.zoneRows(serviceZone)) {
      if (
        row.service?.type !== service?.type ||
        licenseRowState(serviceZone, row) !== 'required'
      ) {
        continue;
      }
      const id = String(row.license?.id ?? '');
      if (!seen.has(id)) {
        seen.add(id);
        result.push(row.license);
      }
    }
    return result;
  }

  // The read view renders one chip per SPECIFIC county but collapses the "All
  // Counties" selection into a single coverage pill.
  isAllCounties(counties: any): boolean {
    return (counties ?? []).some((county: string) => county === 'All Counties');
  }

  // The enabled specialties of a service — the sub-services the vendor turned on
  // for this zone.
  enabledSubTypes(service: any): any[] {
    return (service?.serviceSubTypes ?? []).filter(
      (st: any) => st?.enabledForZone,
    );
  }

  addNewServiceZone() {
    // Only ONE zone form shows at a time. If an in-place Edit form is open, close
    // it before opening the top Add form.
    if (this.editZoneToggle) {
      this.editZoneToggle = false;
      this.editingZone = null;
      this.resetUpdateServiceZoneValues();
    }
    // Snapshot an INDEPENDENT deep copy of the currently offered capabilities as
    // the Add form's working list, with every service + specialty pre-selected.
    this.newZoneServiceTypes = this.cloneOfferedCapabilities(
      this.offeredServiceTemplate,
    );
    for (const serviceType of this.newZoneServiceTypes) {
      serviceType.enabledForZone = true;
      for (const subType of serviceType.serviceSubTypes ?? []) {
        subType.enabledForZone = true;
      }
    }
    this.toggleNewServiceZone = true;
  }

  // Deep-clones an offered-capabilities list (services + their specialties) into
  // a fresh, independent array.
  private cloneOfferedCapabilities(source: any[]): any[] {
    return (source ?? []).map((service: any) => ({
      ...service,
      serviceSubTypes: (service?.serviceSubTypes ?? []).map((subType: any) => ({
        ...subType,
      })),
    }));
  }

  // Builds the Edit picker's working services by reconciling a zone's STORED
  // services against the PA's CURRENT offered capabilities (offeredServiceTemplate).
  private reconcileZoneServicesWithOffered(zoneServices: any[]): any[] {
    const stored = zoneServices ?? [];
    const offered = this.offeredServiceTemplate ?? [];
    const reconciled: any[] = offered.map((offeredService: any) => {
      const storedService = stored.find(
        (s: any) => s.type === offeredService.type,
      );
      return {
        ...offeredService,
        enabledForZone: !!storedService?.enabledForZone,
        serviceSubTypes: (offeredService.serviceSubTypes ?? []).map(
          (subType: any) => {
            const storedSubType = (storedService?.serviceSubTypes ?? []).find(
              (sst: any) => sst.type === subType.type,
            );
            return {
              ...subType,
              enabledForZone: !!storedSubType?.enabledForZone,
            };
          },
        ),
      };
    });
    for (const storedService of stored) {
      const isOffered = offered.some(
        (o: any) => o.type === storedService.type,
      );
      if (storedService?.enabledForZone && !isOffered) {
        reconciled.push({
          ...storedService,
          serviceSubTypes: (storedService.serviceSubTypes ?? [])
            .filter((sst: any) => sst.enabledForZone)
            .map((sst: any) => ({ ...sst })),
        });
      }
    }
    return reconciled;
  }

  // Section 1 running count "N of M services offered".
  get offeredServiceCount(): number {
    return (this.serviceTypes ?? []).filter((s: any) => s.checked).length;
  }

  get totalServiceCount(): number {
    return (this.serviceTypes ?? []).length;
  }

  // Bridges the shared service-capability-picker to the section's existing
  // per-item persistence. Diffs `enabledForZone` against each node's `checked`,
  // mirrors the change onto `checked`, and re-invokes the unchanged save handlers.
  onServiceCapabilitiesChange(services: any[]): void {
    const serviceChanges: any[] = [];
    const subTypeChanges: { service: any; subType: any }[] = [];
    for (const service of services ?? []) {
      if (!!service.enabledForZone !== !!service.checked) {
        serviceChanges.push(service);
      }
      // Only reconcile specialties while the service is (staying) on; turning a
      // service off removes it wholesale server-side.
      if (service.enabledForZone) {
        for (const subType of service.serviceSubTypes ?? []) {
          if (!!subType.enabledForZone !== !!subType.checked) {
            subTypeChanges.push({ service, subType });
          }
        }
      }
    }
    for (const service of serviceChanges) {
      service.checked = !!service.enabledForZone;
    }
    for (const { subType } of subTypeChanges) {
      subType.checked = !!subType.enabledForZone;
    }
    for (const service of serviceChanges) {
      this.saveServiceTypeCheckboxChange(service);
    }
    for (const { service, subType } of subTypeChanges) {
      this.saveSubServiceTypeCheckBoxChange(service, subType);
    }
    this.syncCapabilityPickerFlags();
  }

  // Re-aligns the picker's `enabledForZone` flags with the persisted `checked`
  // state across the (possibly rebuilt) serviceTypes list.
  private syncCapabilityPickerFlags(): void {
    for (const service of this.serviceTypes ?? []) {
      service.enabledForZone = !!service.checked;
      for (const subType of service.serviceSubTypes ?? []) {
        subType.enabledForZone = !!subType.checked;
      }
    }
  }

  editServiceZone(serviceZone: any) {
    // Only ONE zone form shows at a time. If the top Add form is open, close it
    // before opening this in-place Edit form.
    if (this.toggleNewServiceZone) {
      this.toggleNewServiceZone = false;
      this.resetNewServiceZoneValues();
    }
    let currentStateCounty;
    if (serviceZone.nationwide) {
      currentStateCounty = this.serviceZoneStateCountyDetails.find(
        (stateCounty: any) => stateCounty.state === 'NATIONWIDE',
      );
    } else {
      currentStateCounty = this.serviceZoneStateCountyDetails.find(
        (stateCounty: any) => stateCounty.state === serviceZone.state,
      );
    }
    const pendingUpdates = {
      selectedState: currentStateCounty,
      selectedCounties: serviceZone.counties,
      // Reconcile the zone's stored services against the PA's CURRENT offered set
      // so the Edit picker shows only offered services + offered sub-services.
      selectedServices: this.reconcileZoneServicesWithOffered(
        serviceZone.services,
      ),
      zoneDocuments: serviceZone.zoneDocuments,
    };
    this.currentServiceZone = {
      ...pendingUpdates,
      state: serviceZone.nationwide ? 'NATIONWIDE' : serviceZone.state,
    };
    // Track the ORIGINAL loop object (not the cloned working copy) so the
    // template can identity-match it in the *ngFor and render the form in slot.
    this.editingZone = serviceZone;
    this.editZoneToggle = !this.editZoneToggle;
  }

  // ---- Unified Coverage Zone form (Add + Edit share one template) -----------

  get isEditZoneForm(): boolean {
    return this.editZoneToggle;
  }

  get zoneFormTitle(): string {
    if (!this.isEditZoneForm) {
      return 'Add a Zone';
    }
    const state = this.currentServiceZone?.state;
    return (
      'Edit Coverage · ' +
      (state === 'NATIONWIDE' ? 'Nationwide' : this.decodeState(state))
    );
  }

  get formState(): any {
    return this.isEditZoneForm
      ? this.currentServiceZone.selectedState
      : this.selectedNewZoneState;
  }
  set formState(value: any) {
    if (this.isEditZoneForm) {
      this.currentServiceZone.selectedState = value;
    } else {
      this.selectedNewZoneState = value;
    }
  }

  get formCounties(): any {
    return this.isEditZoneForm
      ? this.currentServiceZone.selectedCounties
      : this.selectedNewCounties;
  }
  set formCounties(value: any) {
    if (this.isEditZoneForm) {
      this.currentServiceZone.selectedCounties = value;
    } else {
      this.selectedNewCounties = value;
    }
  }

  get formCountyOptions(): any[] {
    if (this.isEditZoneForm) {
      return this.currentServiceZone?.selectedState?.counties ?? [];
    }
    return this.serviceZoneCounties ?? [];
  }

  get formServices(): any[] {
    return this.isEditZoneForm
      ? this.currentServiceZone.selectedServices
      : this.newZoneServiceTypes;
  }

  handleFormStateChange(): void {
    if (this.isEditZoneForm) {
      this.handleUpdateZoneStateChange();
    } else {
      this.handleNewZoneStateChange();
    }
  }

  saveZoneForm(): void {
    if (this.isEditZoneForm) {
      this.saveUpdatedServiceZone();
    } else {
      this.saveNewServiceZone();
    }
  }

  cancelZoneForm(): void {
    if (this.isEditZoneForm) {
      this.cancelUpdateServiceZone();
    } else {
      this.cancelNewServiceZone();
    }
  }

  saveServiceTypeCheckboxChange(serviceType: any) {
    let deleted = false;
    if (serviceType.checked) {
      this.offeredServiceTemplate = [
        ...this.offeredServiceTemplate,
        { ...serviceType, enabledForZone: false, serviceSubTypes: [] },
      ];
      this.serviceTypes = this.serviceTypes.map((st: any) => {
        if (st.type === serviceType.type) {
          return {
            ...st,
            showSubTypes: serviceType.serviceSubTypes?.length > 0,
          };
        } else {
          return st;
        }
      });
    } else {
      // user is removing service type
      deleted = true;
      this.offeredServiceTemplate = this.offeredServiceTemplate?.filter(
        (currentServiceType: any) =>
          currentServiceType.type !== serviceType.type,
      );
      this.serviceTypes = this.serviceTypes.map((s: any) => {
        if (serviceType.type === s.type) {
          let serviceSubTypes = s.serviceSubTypes;
          if (serviceSubTypes) {
            serviceSubTypes = serviceSubTypes.map((sst: any) => {
              return {
                ...sst,
                checked: false,
              };
            });
          }
          return {
            ...s,
            checked: false,
            showSubTypes: false,
            serviceSubTypes: serviceSubTypes,
          };
        }
        return s;
      });
    }
    this.userService
      .updateServiceCapability(deleted, this.user, serviceType.type)
      .subscribe((response: any) => {
        const apiResponse = response;
        if (apiResponse.status === 'FAILURE') {
          if (apiResponse.message) {
            this.toastr.error(apiResponse.message);
          } else {
            this.toastr.error('An error occurred. Please try again.');
          }
        } else {
          const updatedUser = response.data;
          if (updatedUser) {
            this.user = { ...this.user, ...updatedUser };
            localStorage.setItem('user', JSON.stringify(this.user));
            this.userService.updateUserSubject(this.user);
          } else {
            this.toastr.error('An error occurred. Please try again.');
          }
        }
      });
  }

  saveSubServiceTypeCheckBoxChange(serviceType: any, serviceSubType: any) {
    let deleted = false;
    if (serviceSubType.checked) {
      this.offeredServiceTemplate = this.offeredServiceTemplate.map(
        (st: any) => {
          if (st.type === serviceType.type) {
            if (st.serviceSubTypes?.length > 0) {
              return {
                ...st,
                serviceSubTypes: [
                  ...st.serviceSubTypes,
                  { ...serviceSubType, enabledForZone: false },
                ],
              };
            } else {
              return {
                ...st,
                serviceSubTypes: [
                  { ...serviceSubType, enabledForZone: false },
                ],
              };
            }
          } else {
            return st;
          }
        },
      );
    } else {
      deleted = true;
      this.offeredServiceTemplate = this.offeredServiceTemplate.map(
        (st: any) => {
          if (st.type === serviceType.type) {
            return {
              ...st,
              serviceSubTypes: st.serviceSubTypes?.filter(
                (sst: any) => sst.type !== serviceSubType.type,
              ),
            };
          } else {
            return st;
          }
        },
      );
    }
    this.userService
      .updateServiceSubType(deleted, this.user, serviceType.type, serviceSubType)
      .subscribe((response: any) => {
        const apiResponse = response;
        if (apiResponse.status === 'FAILURE') {
          if (apiResponse.message) {
            this.toastr.error(apiResponse.message);
          } else {
            this.toastr.error('An error occurred. Please try again.');
          }
        } else {
          const updatedUser = response.data;
          if (updatedUser) {
            this.user = { ...this.user, ...updatedUser };
            localStorage.setItem('user', JSON.stringify(this.user));
            this.userService.updateUserSubject(this.user);
          } else {
            this.toastr.error('An error occurred. Please try again.');
          }
        }
      });
  }

  handleNewZoneStateChange() {
    this.serviceZoneCounties = this.selectedNewZoneState.counties;
    this.selectedNewCounties = this.defaultCountiesForState(
      this.serviceZoneCounties,
    );
  }

  handleUpdateZoneStateChange() {
    this.currentServiceZone = {
      ...this.currentServiceZone,
      selectedCounties: this.defaultCountiesForState(
        this.currentServiceZone?.selectedState?.counties,
      ),
    };
  }

  // Shared by both Add and Edit state-change handlers: pre-selects "All Counties"
  // when the newly-chosen state offers that option, otherwise no counties.
  private defaultCountiesForState(counties: any): string[] {
    return counties?.includes('All Counties') ? ['All Counties'] : [];
  }

  isOptionDisabled(counties: any, option: string) {
    return (
      (counties.some((county: string) => county === 'All Counties') &&
        option !== 'All Counties') ||
      (counties.some((county: string) => county !== 'All Counties') &&
        option === 'All Counties')
    );
  }

  saveNewServiceZone() {
    if (!this.selectedNewZoneState || !this.selectedNewZoneState.state) {
      this.toastr.error('Please select a state.');
      return;
    }
    if (
      !this.newZoneServiceTypes.some(
        (serviceType: any) => serviceType.enabledForZone === true,
      )
    ) {
      this.toastr.error('Please select at least one service.');
    } else if (
      this.selectedNewZoneState.state !== 'NATIONWIDE' &&
      this.selectedNewCounties?.length === 0
    ) {
      this.toastr.error('Please select at least one county.');
    } else if (
      this.user?.serviceZones?.some(
        (serviceZone: any) =>
          serviceZone.state === this.selectedNewZoneState.state,
      )
    ) {
      this.toastr.error('A service zone already exists for this state.');
    } else if (
      this.selectedNewZoneState.state === 'NATIONWIDE' &&
      this.user?.serviceZones?.some((serviceZone: any) => serviceZone.nationwide)
    ) {
      this.toastr.error('A nationwide service zone already exists.');
    } else {
      let newServiceZone = {};
      if (this.selectedNewZoneState.state === 'NATIONWIDE') {
        newServiceZone = {
          nationwide: true,
          services: this.newZoneServiceTypes,
        };
      } else {
        newServiceZone = {
          state: this.selectedNewZoneState.state,
          counties: this.selectedNewCounties,
          services: this.newZoneServiceTypes,
        };
      }
      this.userService
        .addNewServiceZone(this.user, newServiceZone)
        .subscribe((response: any) => {
          const apiResponse = response;
          if (apiResponse.status === 'FAILURE') {
            if (apiResponse.message) {
              this.toastr.error(apiResponse.message);
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          } else {
            const updatedUser = response.data;
            if (updatedUser) {
              // Surface the just-added zone at the top of the list. Display-order
              // only — the stored order of record is unchanged.
              if (Array.isArray(updatedUser.serviceZones)) {
                const addedIdx = updatedUser.serviceZones.findIndex(
                  (zone: any) =>
                    (newServiceZone as any).nationwide
                      ? zone.nationwide
                      : zone.state === (newServiceZone as any).state,
                );
                if (addedIdx > 0) {
                  const [added] = updatedUser.serviceZones.splice(addedIdx, 1);
                  updatedUser.serviceZones.unshift(added);
                }
              }
              this.user = { ...this.user, ...updatedUser };
              localStorage.setItem('user', JSON.stringify(this.user));
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          }
        });
      this.toggleNewServiceZone = false;
      this.resetNewServiceZoneValues();
    }
  }

  saveUpdatedServiceZone() {
    if (
      this.currentServiceZone.selectedState.state === 'NATIONWIDE' &&
      this.currentServiceZone.state !== 'NATIONWIDE' &&
      this.user.serviceZones?.some((serviceZone: any) => serviceZone.nationwide)
    ) {
      this.toastr.error('A nationwide service zone already exists.');
    } else if (
      this.currentServiceZone.selectedState.state !==
        this.currentServiceZone.state &&
      this.user.serviceZones?.some(
        (serviceZone: any) =>
          serviceZone.state === this.currentServiceZone.selectedState.state,
      )
    ) {
      this.toastr.error('A service zone already exists for this state.');
    } else if (
      this.currentServiceZone.selectedState.state !== 'NATIONWIDE' &&
      this.currentServiceZone.selectedCounties?.length === 0
    ) {
      this.toastr.error('Please select at least one county');
    } else if (
      !this.currentServiceZone.selectedServices?.some(
        (service: any) => service.enabledForZone,
      )
    ) {
      this.toastr.error('Please select at least one service');
    } else {
      let updatedServiceZone = {};
      if (this.currentServiceZone.selectedState.state === 'NATIONWIDE') {
        updatedServiceZone = {
          nationwide: true,
          services: this.currentServiceZone.selectedServices,
          zoneDocuments: this.currentServiceZone.zoneDocuments,
          serviceSubTypes: this.currentServiceZone.serviceSubTypes,
        };
      } else {
        updatedServiceZone = {
          state: this.currentServiceZone.selectedState.state,
          counties: this.currentServiceZone.selectedCounties,
          services: this.currentServiceZone.selectedServices,
          zoneDocuments: this.currentServiceZone.zoneDocuments,
          serviceSubTypes: this.currentServiceZone.serviceSubTypes,
        };
      }
      this.userService
        .updateServiceZone(
          this.user,
          updatedServiceZone,
          this.currentServiceZone.state,
        )
        .subscribe((response: any) => {
          const apiResponse = response;
          if (apiResponse.status === 'FAILURE') {
            if (apiResponse.message) {
              this.toastr.error(apiResponse.message);
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          } else {
            const updatedUser = response.data;
            if (updatedUser) {
              this.user = { ...this.user, ...updatedUser };
              localStorage.setItem('user', JSON.stringify(this.user));
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          }
        });
      this.editZoneToggle = !this.editZoneToggle;
      this.editingZone = null;
      this.resetUpdateServiceZoneValues();
    }
  }

  removeServiceZone(serviceZone: any) {
    if (this.user.id) {
      let serviceZoneIdentifier;
      if (serviceZone.nationwide) {
        serviceZoneIdentifier = 'NATIONWIDE';
      } else {
        serviceZoneIdentifier = serviceZone.state;
      }
      this.userService
        .removeServiceZone(this.user.id, serviceZoneIdentifier)
        .subscribe((response: any) => {
          const apiResponse = response;
          if (apiResponse.status === 'FAILURE') {
            if (apiResponse.message) {
              this.toastr.error(apiResponse.message);
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          } else {
            const updatedUser = response.data;
            if (updatedUser) {
              this.user = { ...this.user, ...updatedUser };
              localStorage.setItem('user', JSON.stringify(this.user));
              this.toastr.success(apiResponse.message);
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          }
        });
    }
  }

  cancelNewServiceZone() {
    this.toggleNewServiceZone = false;
    this.resetNewServiceZoneValues();
  }

  cancelUpdateServiceZone() {
    this.editZoneToggle = !this.editZoneToggle;
    this.editingZone = null;
    this.resetUpdateServiceZoneValues();
  }

  // Opens the shared upload dialog pre-scoped to a single node. `subType` is null
  // when the node is the service itself. For a LICENSE upload the service is
  // read-only, always-covered context and the "also apply to" checkboxes list
  // ONLY the requirement's configured sub-services under the triggering service.
  openZoneDocUpload(
    serviceZone: any,
    service: any,
    subType: any = null,
    license: LicenseRequirement | null = null,
  ) {
    this.zoneDocUploadZone = serviceZone;
    this.zoneDocUploadService = service;
    this.zoneDocUploadLicense = license;
    this.zoneDocUploadSubmitting = false;
    if (license) {
      this.zoneDocUploadSubType = null;
      const nodesUnderService = licenseNodesUnderService(
        serviceZone,
        this.applicableLicenses,
        this.serviceTypeIdToType,
        license.id,
        service,
      );
      // A service-level node (subType null) makes a service-only upload valid; a
      // sub-only requirement forces >= 1 sub to be checked (see the dialog gate).
      this.zoneDocUploadHasServiceLevelNode = nodesUnderService.some(
        (node) => !node.subType,
      );
      this.zoneDocUploadSiblings = nodesUnderService
        .filter((node) => !!node.subType)
        .map((node) => ({
          type: node.subType.type,
          displayName: node.subType.displayName,
          checked: true,
        }));
    } else {
      this.zoneDocUploadSubType = subType;
      // Legacy uploads have no requirement row to satisfy — gate N/A.
      this.zoneDocUploadHasServiceLevelNode = true;
      this.zoneDocUploadSiblings = this.enabledSubTypes(service)
        .filter((st: any) => !subType || st.type !== subType.type)
        .map((st: any) => ({
          type: st.type,
          displayName: st.displayName,
          checked: false,
        }));
    }
    this.zoneDocUploadOpen = true;
  }

  cancelZoneDocUpload() {
    this.zoneDocUploadOpen = false;
    this.zoneDocUploadZone = null;
    this.zoneDocUploadService = null;
    this.zoneDocUploadSubType = null;
    this.zoneDocUploadSiblings = [];
    this.zoneDocUploadLicense = null;
    this.zoneDocUploadHasServiceLevelNode = true;
    this.zoneDocUploadSubmitting = false;
  }

  // Saves the single-file upload emitted by the shared upload dialog.
  saveZoneDocUpload(payload: ZoneDocUploadResult) {
    if (!payload?.file) {
      this.toastr.error('Please select a file to upload.');
      return;
    }
    const emitted: ZoneDocCoverage | null = payload.coverage;
    const coverage = {
      serviceType: emitted?.serviceType ?? this.zoneDocUploadService?.type,
      serviceSubTypes: emitted?.serviceSubTypes ?? [],
      expirationDate: emitted?.expirationDate
        ? new Date(emitted.expirationDate)
        : null,
      licenseRequirementId:
        emitted?.licenseRequirementId ?? this.zoneDocUploadLicense?.id ?? null,
      issueDate: emitted?.issueDate ? new Date(emitted.issueDate) : null,
    };
    const serviceZone = this.zoneDocUploadZone;
    // Single file only — wrap it in a FileList-like array for the upload service.
    const files = [payload.file] as unknown as FileList;
    this.zoneDocUploadSubmitting = true;
    this.toastr.info('Uploading service zone documents...');
    this.documentService
      .uploadServiceZoneDocuments(this.user, serviceZone, files, coverage)
      .subscribe({
        next: (response: any) => {
          const apiResponse = response;
          if (apiResponse.status === 'FAILURE') {
            this.zoneDocUploadSubmitting = false;
            this.toastr.error(
              apiResponse.message || 'An error occurred. Please try again.',
            );
          } else {
            const updatedUser = response.data;
            if (updatedUser) {
              this.user = { ...this.user, ...updatedUser };
              localStorage.setItem('user', JSON.stringify(this.user));
              this.toastr.success(apiResponse.message);
              this.cancelZoneDocUpload();
            } else {
              this.zoneDocUploadSubmitting = false;
              this.toastr.error('An error occurred. Please try again.');
            }
          }
        },
        error: () => {
          this.zoneDocUploadSubmitting = false;
          this.toastr.error('An error occurred. Please try again.');
        },
      });
  }

  private resetNewServiceZoneValues() {
    this.selectedNewZoneState = '';
    this.selectedNewCounties = [];
    this.newZoneServiceTypes = this.newZoneServiceTypes.map(
      (currentServiceType: any) => {
        return { ...currentServiceType, enabledForZone: false };
      },
    );
  }

  private resetUpdateServiceZoneValues() {
    this.currentServiceZone = {};
  }
}
